/**
 * TutorialManager.tsx
 *
 * Provides the TourContext (for backwards compatibility with HelpDropdownMenu,
 * LearningCenterPage, and any other consumers) around the react-joyride engine.
 * All mutable tutorial state lives in Redux (tutorialSlice).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Joyride,
  EventData,
  ACTIONS,
  EVENTS,
  STATUS,
  PartialDeep,
  Step,
  Styles,
} from "react-joyride";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { AppDispatch, RootState } from "../store/store";
import {
  completeTutorial,
  setAllProgress,
  setMonitorHighlightDevice,
  setStep,
  skipTutorial as skipTutorialAction,
  startTutorial as startTutorialAction,
  TutorialId,
  TutorialStatus,
} from "../store/slices/tutorialSlice";
import { TUTORIAL_REGISTRY, TUTORIALS } from "./TutorialRegistry";
import {
  DeviceOnboardingDeployment,
  DeviceOnboardingStepsContext,
  getDeviceOnboardingSteps,
  isDeviceOnboardingWizardStep,
  SANDBOX_URL,
  tutorialSteps,
} from "./TutorialSteps";

// ---------------------------------------------------------------------------
// Context API (thin wrapper over dispatch so consumers don't import Redux)
// ---------------------------------------------------------------------------

interface TutorialStateSnapshot {
  id: TutorialId;
  title: string;
  description: string;
  totalSteps: number;
  status: TutorialStatus;
  currentStep: number;
}

interface TourContextType {
  /** Start (or resume) the legacy fleet-onboarding tutorial */
  start: () => void;
  /** Restart a tutorial from step 0 */
  reset: (tutorialId?: TutorialId) => void;
  /** Skip / cancel a tutorial */
  cancel: (tutorialId?: TutorialId) => void;
  /** Start (or resume) any tutorial by ID */
  startTutorial: (tutorialId: TutorialId) => void;
  /** Resume from saved step */
  resumeTutorial: (tutorialId: TutorialId) => void;
  /** Mark a tutorial as skipped without launching it */
  skipTutorial: (tutorialId: TutorialId) => void;
  /** Currently active tutorial, or null */
  currentTutorial: TutorialId | null;
  /** Snapshot list for LearningCenterPage */
  tutorials: TutorialStateSnapshot[];
  getTutorialState: (tutorialId: TutorialId) => TutorialStateSnapshot;
  isActive: boolean;
  /** Kept for backwards compat; always false in the Joyride-based engine */
  showResumeDialog: false;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) throw new Error("useTour must be used within a TourProvider");
  return context;
};

// ---------------------------------------------------------------------------
// Joyride theme
// ---------------------------------------------------------------------------

const joyrideStyles: PartialDeep<Styles> = {
  // ---- Tooltip card ----
  tooltip: {
    backgroundColor: "#171717",
    borderRadius: "0.875rem",
    padding: "0",
    border: "1px solid #404040",
    boxShadow:
      "0 20px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset",
    maxWidth: 400,
    overflow: "hidden",
  },
  tooltipContainer: {
    padding: "1.125rem 1.25rem 1rem",
  },
  tooltipTitle: {
    fontSize: "0.9375rem",
    fontWeight: "700",
    color: "#f5f5f5",
    marginBottom: "0.5rem",
    letterSpacing: "-0.01em",
  },
  tooltipContent: {
    fontSize: "0.8125rem",
    lineHeight: "1.55",
    color: "#a3a3a3",
  },
  // ---- Footer bar ----
  tooltipFooter: {
    backgroundColor: "#1f1f1f",
    borderTop: "1px solid #2a2a2a",
    padding: "0.625rem 1.25rem",
    gap: "0.5rem",
  },
  tooltipFooterSpacer: {
    flex: "1",
  },
  // ---- Buttons ----
  // Primary matches the app's default Button: rose-500 → amber-600 gradient
  buttonPrimary: {
    background: "linear-gradient(to right, #f43f5e, #d97706)",
    color: "#fff",
    borderRadius: "0.5rem",
    fontWeight: "600",
    fontSize: "0.8125rem",
    padding: "0.375rem 0.875rem",
    border: "none",
    cursor: "pointer",
  },
  buttonBack: {
    color: "#a3a3a3",
    background: "transparent",
    border: "none",
    fontSize: "0.8125rem",
    fontWeight: "500",
    cursor: "pointer",
    padding: "0.375rem 0.5rem",
  },
  buttonSkip: {
    color: "#6b7280",
    background: "transparent",
    border: "none",
    fontSize: "0.8125rem",
    fontWeight: "500",
    cursor: "pointer",
    padding: "0.375rem 0.5rem",
  },
  buttonClose: {
    color: "#a3a3a3",
    background: "transparent",
    border: "none",
    fontSize: "0.875rem",
    lineHeight: "1",
    cursor: "pointer",
    top: "0.5rem",
    right: "0.625rem",
    padding: "0.125rem",
    borderRadius: "0.375rem",
    width: "1.25rem",
    height: "1.25rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  // ---- Overlay ----
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
  // ---- Beacon: orange-500 matches the app's accent color ----
  beaconInner: {
    backgroundColor: "#f97316",
  },
  beaconOuter: {
    backgroundColor: "rgba(249, 115, 22, 0.2)",
    border: "2px solid #f97316",
  },
};

const joyrideOptions = {
  zIndex: 10000,
  primaryColor: "#f97316", // orange-500 (brand accent)
  backgroundColor: "#171717",
  textColor: "#e5e5e5",
  arrowColor: "#171717",
  buttons: ["back", "close", "primary", "skip"] as const,
  overlayClickAction: false as const,
  // Clicking the X button should always cancel the tour, not advance to the
  // next step (which would complete the tour if at the last step).
  closeButtonAction: "skip" as const,
} satisfies Partial<import("react-joyride").Options>;

// ---------------------------------------------------------------------------
// Per-tutorial localStorage step key (matches previous implementation)
// ---------------------------------------------------------------------------

const stepKey = (id: TutorialId) => `insaion_tutorial_${id}_step`;
const deviceOnboardingStateKey = "insaion_tutorial_device_onboarding_state";
const helpCenterHighlightEvent = "insaion:highlightHelpCenter";

const defaultDeviceOnboardingState = (): DeviceOnboardingStepsContext => ({
  deploymentChoice: null,
  registeredDeviceId: null,
});

const saveStep = (id: TutorialId, step: number) => {
  try {
    localStorage.setItem(stepKey(id), String(step));
  } catch {
    /* ignore */
  }
};

const clearStep = (id: TutorialId) => {
  try {
    localStorage.removeItem(stepKey(id));
  } catch {
    /* ignore */
  }
};

const loadDeviceOnboardingState = (): DeviceOnboardingStepsContext => {
  try {
    const raw = localStorage.getItem(deviceOnboardingStateKey);
    if (!raw) return defaultDeviceOnboardingState();
    return {
      ...defaultDeviceOnboardingState(),
      ...(JSON.parse(raw) as Partial<DeviceOnboardingStepsContext>),
    };
  } catch {
    return defaultDeviceOnboardingState();
  }
};

const saveDeviceOnboardingState = (state: DeviceOnboardingStepsContext) => {
  try {
    localStorage.setItem(deviceOnboardingStateKey, JSON.stringify(state));
  } catch {
    /* ignore */
  }
};

const clearDeviceOnboardingState = () => {
  try {
    localStorage.removeItem(deviceOnboardingStateKey);
  } catch {
    /* ignore */
  }
};

const pulseHelpCenter = () => {
  window.dispatchEvent(new Event(helpCenterHighlightEvent));
};

const isStepTargetReady = (step: Step | undefined): boolean => {
  if (!step) return false;

  const { target } = step;

  if (typeof target === "string") {
    if (target === "body") return true;
    try {
      return document.querySelector(target) !== null;
    } catch {
      return false;
    }
  }

  return Boolean(target);
};

// ---------------------------------------------------------------------------
// Backend sync helper  (maps ID → legacy backend name: "main" for Module 1)
// ---------------------------------------------------------------------------

const backendName = (id: TutorialId): string =>
  id === "device_onboarding" ? "main" : id;

const syncStatus = async (
  id: TutorialId,
  status: Exclude<TutorialStatus, "NONE">,
) => {
  try {
    await apiClient.post(`/onboarding/${backendName(id)}`, { status });
  } catch (e) {
    console.warn("[Tutorial] Failed to sync status:", e);
  }
};

const fetchAllStatuses = async (): Promise<
  Partial<Record<TutorialId, TutorialStatus>>
> => {
  const result: Partial<Record<TutorialId, TutorialStatus>> = {};
  await Promise.all(
    TUTORIALS.map(async (t) => {
      try {
        const { data } = await apiClient.get<{ status: TutorialStatus }>(
          `/onboarding/${backendName(t.id)}`,
        );
        result[t.id] = data?.status ?? "NONE";
      } catch {
        result[t.id] = "NONE";
      }
    }),
  );
  return result;
};

// ---------------------------------------------------------------------------
// Cross-page navigation map: which route to push before a given step
// ---------------------------------------------------------------------------

type RouteMap = Record<
  TutorialId,
  /** key = step index that requires navigation BEFORE rendering */
  Partial<Record<number, string>>
>;

const ROUTE_MAP: RouteMap = {
  device_onboarding: {},
  device_configuration: {
    1: "/devices",
  },
  dashboard_panels: {
    // Navigate to /monitoring when going to (or back to) any of the
    // monitoring-page steps so the dashboard header / sidebar are visible.
    0: "/monitoring",
    1: "/monitoring",
    2: "/monitoring",
  },
  lichtblick_recordings: {
    1: "/devices",
  },
};

// ---------------------------------------------------------------------------
// Provider + Engine
// ---------------------------------------------------------------------------

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useSelector((s: RootState) => s.appAuthReducer.user?.id);
  const [deviceOnboardingState, setDeviceOnboardingState] = useState(
    loadDeviceOnboardingState,
  );
  const loadedStatusesForUserRef = useRef<string | null>(null);

  const { run, stepIndex, activeTutorial, progress } = useSelector(
    (s: RootState) => s.tutorialReducer,
  );

  const persistDeviceOnboardingState = useCallback(
    (
      updater:
        | DeviceOnboardingStepsContext
        | ((
            current: DeviceOnboardingStepsContext,
          ) => DeviceOnboardingStepsContext),
    ) => {
      let resolvedState = defaultDeviceOnboardingState();
      setDeviceOnboardingState((current) => {
        resolvedState =
          typeof updater === "function" ? updater(current) : updater;
        saveDeviceOnboardingState(resolvedState);
        return resolvedState;
      });
      return resolvedState;
    },
    [],
  );

  const currentSteps = useMemo(() => {
    if (activeTutorial === "device_onboarding") {
      return getDeviceOnboardingSteps(deviceOnboardingState);
    }
    return activeTutorial ? tutorialSteps[activeTutorial] : [];
  }, [activeTutorial, deviceOnboardingState]);

  const goToDeviceOnboardingStep = useCallback(
    (
      stepId: string,
      nextState: DeviceOnboardingStepsContext = deviceOnboardingState,
    ) => {
      const nextSteps = getDeviceOnboardingSteps(nextState);
      const nextIndex = nextSteps.findIndex((step) => step.id === stepId);
      if (nextIndex === -1) return;
      dispatch(setStep(nextIndex));
      saveStep("device_onboarding", nextIndex);
    },
    [deviceOnboardingState, dispatch],
  );

  /**
   * Opens the Add Device wizard with nothing pre-picked: the tutorial now walks
   * the user through every section, starting with the platform choice. Skips the
   * navigation when already there so the wizard keeps the state it has.
   */
  const openDeviceWizard = useCallback(() => {
    if (location.pathname === "/devices/new") return;
    navigate("/devices/new");
  }, [location.pathname, navigate]);

  const showTutorialSkippedToast = useCallback(() => {
    toast("Tutorial skipped. Restart anytime in Learning Center.", {
      action: {
        label: "Learning Center",
        onClick: () => navigate("/learning-center"),
      },
    });
  }, [navigate]);

  // Load remote statuses after user identity is available.
  useEffect(() => {
    if (!userId) return;
    if (loadedStatusesForUserRef.current === userId) return;

    loadedStatusesForUserRef.current = userId;
    fetchAllStatuses().then((statuses) => {
      const updates: Parameters<typeof setAllProgress>[0] = {};
      for (const t of TUTORIALS) {
        clearStep(t.id);
        updates[t.id] = {
          status: statuses[t.id] ?? "NONE",
          currentStep: 0,
        };
      }
      dispatch(setAllProgress(updates));
    });
  }, [dispatch, userId]);

  const scrollTutorialTargetIntoView = useCallback((targetSelector: string) => {
    let attempts = 0;
    const maxAttempts = 24;

    const tryScroll = () => {
      const target = document.querySelector(
        targetSelector,
      ) as HTMLElement | null;

      if (!target) {
        attempts += 1;
        if (attempts < maxAttempts) {
          window.requestAnimationFrame(tryScroll);
        }
        return;
      }

      // Use native scrollIntoView - works with any scroll ancestor (window,
      // internal overflow containers, etc.).
      target.scrollIntoView({ block: "start", behavior: "smooth" });

      // Fire resize events on a timing ladder so Joyride repositions its
      // spotlight after each scroll frame settles.
      window.dispatchEvent(new Event("resize"));
      window.setTimeout(() => window.dispatchEvent(new Event("resize")), 150);
      window.setTimeout(() => window.dispatchEvent(new Event("resize")), 400);
      window.setTimeout(() => window.dispatchEvent(new Event("resize")), 700);
    };

    tryScroll();
  }, []);

  // Event-driven step advancement (SSE → custom DOM events)
  useEffect(() => {
    const handleDeploymentSelected = (event: Event) => {
      if (activeTutorial !== "device_onboarding" || !run) return;

      const choice = (event as CustomEvent).detail?.choice as
        | DeviceOnboardingDeployment
        | undefined;
      if (!choice) return;

      if (choice === "own_robot") {
        const nextState = persistDeviceOnboardingState((current) => ({
          ...current,
          deploymentChoice: "own_robot",
          registeredDeviceId: null,
        }));

        openDeviceWizard();
        goToDeviceOnboardingStep("own-platform", nextState);
        return;
      }

      const nextState = persistDeviceOnboardingState((current) => ({
        ...current,
        deploymentChoice: "sandbox",
        registeredDeviceId: null,
      }));

      // Opened in a new tab so this session — and the tutorial itself — stays
      // put; the click that dispatched this event still counts as user
      // activation, so the popup blocker lets it through.
      window.open(SANDBOX_URL, "_blank", "noopener,noreferrer");
      goToDeviceOnboardingStep("sandbox-redirect", nextState);
    };

    // Picking a platform is the only wizard section the tutorial advances for
    // the user; the rest are theirs to fill in at their own pace.
    const handlePlatformSelected = () => {
      if (activeTutorial !== "device_onboarding" || !run) return;
      if (deviceOnboardingState.deploymentChoice !== "own_robot") return;
      if (currentSteps[stepIndex]?.id !== "own-platform") return;

      goToDeviceOnboardingStep("own-enrollment-key");
    };

    // Step 5 of the wizard watches for the agent on its own. Its detection is
    // what carries the tutorial past the install command.
    const handleAgentDetected = (event: Event) => {
      if (activeTutorial !== "device_onboarding" || !run) return;
      if (deviceOnboardingState.deploymentChoice !== "own_robot") return;
      if (!isDeviceOnboardingWizardStep(currentSteps[stepIndex]?.id)) return;

      const deviceId = (event as CustomEvent).detail?.deviceId as
        | string
        | undefined;
      if (!deviceId) return;
      if (deviceOnboardingState.registeredDeviceId === deviceId) return;

      const nextState = persistDeviceOnboardingState((current) => ({
        ...current,
        registeredDeviceId: deviceId,
      }));

      dispatch(setMonitorHighlightDevice(deviceId));
      goToDeviceOnboardingStep("own-agent-status", nextState);
      toast.success(`Agent connected! Device ID: ${deviceId}`);
    };

    const handleMonitorOpened = async () => {
      if (activeTutorial !== "device_onboarding" || !run) return;

      dispatch(setMonitorHighlightDevice(null));
      goToDeviceOnboardingStep("tutorial-complete");
    };

    const handleTopicConfigured = () => {
      if (activeTutorial !== "device_configuration" || !run) return;
      // Step 3 = config-monitored-sources; advance to the recording settings
      if (stepIndex === 3) dispatch(setStep(4));
    };

    const handleDeviceDetailsReady = () => {
      if (!run) return;

      if (activeTutorial === "device_configuration") {
        // Only jump to the "navigate-to-sources" step (2) if not already past it
        if (stepIndex >= 3) return;

        dispatch(setStep(2));
        saveStep("device_configuration", 2);
        return;
      }

      if (activeTutorial === "lichtblick_recordings") {
        if (stepIndex >= 3) return;

        dispatch(setStep(2));
        saveStep("lichtblick_recordings", 2);
      }
    };

    const handleDeviceSelectedFromTable = () => {
      if (!run) return;

      if (activeTutorial === "device_configuration") {
        if (stepIndex !== 1) return;

        // Jump to the new "navigate-to-sources" wizard step
        dispatch(setStep(2));
        saveStep("device_configuration", 2);
      }

      if (activeTutorial === "lichtblick_recordings") {
        if (stepIndex !== 1) return;

        dispatch(setStep(2));
        saveStep("lichtblick_recordings", 2);
      }
    };

    const handleConfigSectionChanged = (e: Event) => {
      if (activeTutorial !== "device_configuration" || !run) return;
      const section = (e as CustomEvent).detail?.section as string | undefined;
      if (!section) return;

      // Step 2 = config-navigate-to-sources: user clicked Data Collection.
      // Streaming and recording now share this one section, so the later
      // recording steps advance with their own Next button.
      if (section === "sources" && stepIndex === 2) {
        dispatch(setStep(3));
        saveStep("device_configuration", 3);
      }
    };

    const handlePanelCreated = () => {
      if (activeTutorial !== "dashboard_panels" || !run) return;
      // Step 6 = "Save the Panel" — advance to the completion step.
      if (stepIndex === 6) dispatch(setStep(7));
    };

    const handleAddPanelOpened = () => {
      if (activeTutorial !== "dashboard_panels" || !run) return;
      // Step 2 = "Add a Panel" (wizard-driven) — advance to the panel-config steps.
      if (stepIndex === 2) {
        dispatch(setStep(3));
        saveStep("dashboard_panels", 3);
      }
    };

    const handleRecordingsTabOpened = () => {
      if (activeTutorial !== "lichtblick_recordings" || !run) return;
      if (stepIndex !== 2) return;

      dispatch(setStep(3));
      saveStep("lichtblick_recordings", 3);
      window.requestAnimationFrame(() => {
        scrollTutorialTargetIntoView("#tutorial-recordings-timeline");
      });
    };

    const handleRecordingSelectionReady = () => {
      if (activeTutorial !== "lichtblick_recordings" || !run) return;
      if (stepIndex !== 3) return;

      dispatch(setStep(4));
      saveStep("lichtblick_recordings", 4);
    };

    const handleRecordingUploadRequested = () => {
      if (activeTutorial !== "lichtblick_recordings" || !run) return;
      if (stepIndex !== 4) return;

      dispatch(setStep(5));
      saveStep("lichtblick_recordings", 5);
      window.requestAnimationFrame(() => {
        scrollTutorialTargetIntoView("#tutorial-recordings-list");
      });
    };

    const handleLichtblickOpened = () => {
      if (activeTutorial !== "lichtblick_recordings" || !run) return;
      if (stepIndex !== 5) return;

      dispatch(setStep(6));
      saveStep("lichtblick_recordings", 6);
    };

    window.addEventListener(
      "insaion:tutorialDeviceOnboardingDeploymentSelected",
      handleDeploymentSelected,
    );
    window.addEventListener(
      "insaion:addDevicePlatformSelected",
      handlePlatformSelected,
    );
    window.addEventListener(
      "insaion:tutorialAgentDetected",
      handleAgentDetected,
    );
    window.addEventListener(
      "insaion:tutorialMonitorOpened",
      handleMonitorOpened,
    );
    window.addEventListener(
      "insaion:tutorialDeviceDetailsReady",
      handleDeviceDetailsReady,
    );
    window.addEventListener(
      "insaion:tutorialDeviceSelectedFromTable",
      handleDeviceSelectedFromTable,
    );
    window.addEventListener("insaion:topicConfigured", handleTopicConfigured);
    window.addEventListener(
      "insaion:configSectionChanged",
      handleConfigSectionChanged,
    );
    window.addEventListener("insaion:panelCreated", handlePanelCreated);
    window.addEventListener("insaion:addPanelOpened", handleAddPanelOpened);
    window.addEventListener(
      "insaion:tutorialRecordingsTabOpened",
      handleRecordingsTabOpened,
    );
    window.addEventListener(
      "insaion:tutorialRecordingSelectionReady",
      handleRecordingSelectionReady,
    );
    window.addEventListener(
      "insaion:tutorialRecordingUploadRequested",
      handleRecordingUploadRequested,
    );
    window.addEventListener("insaion:lichtblickOpened", handleLichtblickOpened);
    return () => {
      window.removeEventListener(
        "insaion:tutorialDeviceOnboardingDeploymentSelected",
        handleDeploymentSelected,
      );
      window.removeEventListener(
        "insaion:addDevicePlatformSelected",
        handlePlatformSelected,
      );
      window.removeEventListener(
        "insaion:tutorialAgentDetected",
        handleAgentDetected,
      );
      window.removeEventListener(
        "insaion:tutorialMonitorOpened",
        handleMonitorOpened,
      );
      window.removeEventListener(
        "insaion:tutorialDeviceDetailsReady",
        handleDeviceDetailsReady,
      );
      window.removeEventListener(
        "insaion:tutorialDeviceSelectedFromTable",
        handleDeviceSelectedFromTable,
      );
      window.removeEventListener(
        "insaion:topicConfigured",
        handleTopicConfigured,
      );
      window.removeEventListener(
        "insaion:configSectionChanged",
        handleConfigSectionChanged,
      );
      window.removeEventListener("insaion:panelCreated", handlePanelCreated);
      window.removeEventListener(
        "insaion:addPanelOpened",
        handleAddPanelOpened,
      );
      window.removeEventListener(
        "insaion:tutorialRecordingsTabOpened",
        handleRecordingsTabOpened,
      );
      window.removeEventListener(
        "insaion:tutorialRecordingSelectionReady",
        handleRecordingSelectionReady,
      );
      window.removeEventListener(
        "insaion:tutorialRecordingUploadRequested",
        handleRecordingUploadRequested,
      );
      window.removeEventListener(
        "insaion:lichtblickOpened",
        handleLichtblickOpened,
      );
    };
  }, [
    activeTutorial,
    currentSteps,
    deviceOnboardingState.deploymentChoice,
    deviceOnboardingState.registeredDeviceId,
    dispatch,
    goToDeviceOnboardingStep,
    navigate,
    openDeviceWizard,
    persistDeviceOnboardingState,
    run,
    scrollTutorialTargetIntoView,
    stepIndex,
  ]);

  // Joyride callback — the single control point for all user-driven transitions
  const handleCallback = useCallback(
    async (data: EventData) => {
      const { action, index, status, type } = data;

      if (!activeTutorial) return;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        const isCompleted = status === STATUS.FINISHED;
        if (isCompleted) {
          dispatch(completeTutorial(activeTutorial));
          clearStep(activeTutorial);
          if (activeTutorial === "device_onboarding") {
            clearDeviceOnboardingState();
            setDeviceOnboardingState(defaultDeviceOnboardingState());
          }
          await syncStatus(activeTutorial, "COMPLETED");
          toast.success("Tutorial completed!");
        } else {
          dispatch(skipTutorialAction(activeTutorial));
          clearStep(activeTutorial);
          if (activeTutorial === "device_onboarding") {
            clearDeviceOnboardingState();
            setDeviceOnboardingState(defaultDeviceOnboardingState());
          }
          await syncStatus(activeTutorial, "SKIPPED");
          pulseHelpCenter();
          showTutorialSkippedToast();
        }
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
        if (nextIndex < 0) return;

        const nextStep = currentSteps[nextIndex];
        const routeForStep = ROUTE_MAP[activeTutorial]?.[nextIndex];

        // Route first when required so target checks happen in the right page.
        if (routeForStep) {
          navigate(routeForStep);
        }

        if (
          action !== ACTIONS.PREV &&
          activeTutorial !== "device_onboarding" &&
          !!nextStep &&
          !routeForStep &&
          !isStepTargetReady(nextStep)
        ) {
          toast.info(
            "Complete the current instruction first, then continue once the next UI element is visible.",
          );
          return;
        }

        if (activeTutorial === "device_onboarding") {
          // Every own-robot step is a section of the Add Device wizard, so make
          // sure the page is open before the spotlight looks for its target.
          if (isDeviceOnboardingWizardStep(nextStep?.id)) {
            openDeviceWizard();
          }

          if (nextStep?.id === "registered-monitor") {
            navigate("/devices");
          }
        }

        dispatch(setStep(nextIndex));
        saveStep(activeTutorial, nextIndex);

        if (
          (activeTutorial === "device_configuration" ||
            activeTutorial === "lichtblick_recordings" ||
            isDeviceOnboardingWizardStep(nextStep?.id)) &&
          typeof nextStep?.target === "string" &&
          nextStep.target !== "body"
        ) {
          const nextTargetSelector = nextStep.target;
          window.requestAnimationFrame(() => {
            scrollTutorialTargetIntoView(nextTargetSelector);
          });
        }

        return;
      }

      if (
        type === EVENTS.TARGET_NOT_FOUND &&
        activeTutorial !== "device_onboarding"
      ) {
        // Keep the current step when a target is temporarily absent.
        // This prevents automatic cascade to the end of the tutorial.
        return;
      }
    },
    [
      activeTutorial,
      currentSteps,
      dispatch,
      navigate,
      openDeviceWizard,
      scrollTutorialTargetIntoView,
      showTutorialSkippedToast,
    ],
  );

  // --------------------------------------------------------------------------
  // Context API helpers
  // --------------------------------------------------------------------------

  const launchTutorial = useCallback(
    (id: TutorialId, step = 0) => {
      if (id === "device_onboarding") {
        clearDeviceOnboardingState();
        setDeviceOnboardingState(defaultDeviceOnboardingState());
      }
      clearStep(id);
      dispatch(startTutorialAction({ id, step }));
      saveStep(id, step);
      void syncStatus(id, "IN_PROGRESS");
    },
    [dispatch],
  );

  // "Continue to Next Tutorial" button inside step content
  useEffect(() => {
    const handleStartNext = async () => {
      if (!activeTutorial || !run) return;

      const currentIndex = TUTORIALS.findIndex((t) => t.id === activeTutorial);
      const nextTutorial = TUTORIALS[currentIndex + 1];
      if (!nextTutorial) return;

      const currentId = activeTutorial;

      dispatch(completeTutorial(currentId));
      clearStep(currentId);
      if (currentId === "device_onboarding") {
        clearDeviceOnboardingState();
        setDeviceOnboardingState(defaultDeviceOnboardingState());
      }
      void syncStatus(currentId, "COMPLETED");
      toast.success("Tutorial completed!");

      // Wait one frame so Joyride can unmount before the next tutorial mounts
      setTimeout(() => {
        launchTutorial(nextTutorial.id, 0);
      }, 100);
    };

    window.addEventListener("insaion:tutorialStartNext", handleStartNext);
    return () =>
      window.removeEventListener("insaion:tutorialStartNext", handleStartNext);
  }, [activeTutorial, dispatch, launchTutorial, run]);

  const startTutorial = useCallback(
    (id: TutorialId) => {
      launchTutorial(id, 0);
    },
    [launchTutorial],
  );

  const resumeTutorial = useCallback(
    (id: TutorialId) => {
      launchTutorial(id, 0);
    },
    [launchTutorial],
  );

  const doSkip = useCallback(
    (id: TutorialId) => {
      dispatch(skipTutorialAction(id));
      clearStep(id);
      if (id === "device_onboarding") {
        clearDeviceOnboardingState();
        setDeviceOnboardingState(defaultDeviceOnboardingState());
      }
      void syncStatus(id, "SKIPPED");
      pulseHelpCenter();
      showTutorialSkippedToast();
    },
    [dispatch, showTutorialSkippedToast],
  );

  const reset = useCallback(
    (id: TutorialId = activeTutorial ?? "device_onboarding") => {
      clearStep(id);
      if (id === "device_onboarding") {
        clearDeviceOnboardingState();
        setDeviceOnboardingState(defaultDeviceOnboardingState());
      }
      launchTutorial(id, 0);
    },
    [activeTutorial, launchTutorial],
  );

  const cancel = useCallback(
    (id: TutorialId = activeTutorial ?? "device_onboarding") => {
      doSkip(id);
    },
    [activeTutorial, doSkip],
  );

  // Tutorials snapshot for LearningCenterPage
  useEffect(() => {
    if (activeTutorial !== "device_configuration" || !run) return;

    const currentStepId = currentSteps[stepIndex]?.id;
    if (currentStepId !== "config-open-device") return;

    let attempts = 0;
    const maxAttempts = 15;
    const intervalId = window.setInterval(() => {
      const table = document.querySelector("#tutorial-devices-table");
      if (!table) {
        attempts += 1;
        if (attempts >= maxAttempts) {
          window.clearInterval(intervalId);
        }
        return;
      }

      const rowCount = table.querySelectorAll("tbody tr").length;
      if (rowCount === 0) {
        window.clearInterval(intervalId);
        toast.info(
          "No registered devices found yet. Register a device first, then restart Module 2 from Learning Center.",
        );
        doSkip("device_configuration");
        return;
      }

      window.clearInterval(intervalId);
    }, 250);

    return () => window.clearInterval(intervalId);
  }, [activeTutorial, currentSteps, doSkip, run, stepIndex]);

  // The Add Device wizard is one long scrollable page, so every onboarding step
  // has to bring its own section into view — including the ones the wizard
  // itself advances (platform picked, agent detected).
  useEffect(() => {
    if (activeTutorial !== "device_onboarding" || !run) return;

    const currentStep = currentSteps[stepIndex];
    if (!isDeviceOnboardingWizardStep(currentStep?.id)) return;
    if (
      typeof currentStep?.target !== "string" ||
      currentStep.target === "body"
    ) {
      return;
    }

    scrollTutorialTargetIntoView(currentStep.target);
  }, [
    activeTutorial,
    currentSteps,
    run,
    scrollTutorialTargetIntoView,
    stepIndex,
  ]);

  useEffect(() => {
    if (activeTutorial !== "device_configuration" || !run) return;

    const currentStep = currentSteps[stepIndex];
    const currentStepId = currentStep?.id;
    const scrolledSteps = [
      "config-monitored-sources",
      "config-recording-settings",
      "config-rolling-buffer-basics",
      "config-rolling-buffer-pipelines",
    ];
    if (!currentStepId || !scrolledSteps.includes(currentStepId)) {
      return;
    }

    if (
      typeof currentStep.target !== "string" ||
      currentStep.target === "body"
    ) {
      return;
    }

    scrollTutorialTargetIntoView(currentStep.target);
  }, [
    activeTutorial,
    currentSteps,
    run,
    scrollTutorialTargetIntoView,
    stepIndex,
  ]);

  const tutorialsSnapshot = useMemo<TutorialStateSnapshot[]>(
    () =>
      TUTORIALS.map((t) => ({
        id: t.id,
        title: t.title,
        description: t.description,
        totalSteps: t.totalSteps,
        status: progress[t.id].status,
        currentStep: progress[t.id].currentStep,
      })),
    [progress],
  );

  const getTutorialState = useCallback(
    (id: TutorialId): TutorialStateSnapshot => {
      const t = TUTORIAL_REGISTRY[id];
      return {
        id,
        title: t.title,
        description: t.description,
        totalSteps: t.totalSteps,
        status: progress[id].status,
        currentStep: progress[id].currentStep,
      };
    },
    [progress],
  );

  const steps = currentSteps;

  return (
    <TourContext.Provider
      value={{
        start: () => startTutorial("device_onboarding"),
        reset,
        cancel,
        startTutorial,
        resumeTutorial,
        skipTutorial: doSkip,
        currentTutorial: activeTutorial,
        tutorials: tutorialsSnapshot,
        getTutorialState,
        isActive: run,
        showResumeDialog: false,
      }}
    >
      <Joyride
        onEvent={handleCallback}
        continuous
        run={run}
        stepIndex={stepIndex}
        steps={steps}
        options={joyrideOptions}
        styles={joyrideStyles}
        locale={{ last: "Done" }}
      />
      {children}
    </TourContext.Provider>
  );
};
